import { z } from 'zod';
import {
  BillingRequestKind,
  BillingRequestStatus,
  BillingService,
  SubscriptionStatus,
  ServiceStatus,
} from '../generated/prisma/enums.ts';
import { idSchema } from './content-primitives.ts';

export const paymentMethodSchema = z.object({
  brand: z.string(),
  last4: z.string(),
  expMonth: z.number().int(),
  expYear: z.number().int(),
});

const paymentMethodBrands: Record<string, string> = {
  amex: 'American Express',
  cartes_bancaires: 'Cartes Bancaires',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'eftpos',
  jcb: 'JCB',
  link: 'Link',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

export function paymentMethodBrand(brand: string) {
  return (
    paymentMethodBrands[brand] ??
    brand.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}
export const billingOperationSchema = z.object({
  id: idSchema,
  accountId: idSchema,
  siteId: idSchema.nullable(),
  kind: z.enum(BillingRequestKind),
  service: z.enum(BillingService),
  status: z.enum(BillingRequestStatus),
  invoiceId: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.iso.datetime().meta({ pattern: undefined }),
  scheduledFor: z.iso.datetime().meta({ pattern: undefined }).nullable(),
});
export const billingAccountSchema = z.object({
  id: idSchema,
  owner: z.boolean(),
  ownerName: z.string(),
  ownerEmail: z.email(),
  stripeSubscriptionStatus: z.enum(SubscriptionStatus).nullable(),
  subscriptionId: z.string().nullable(),
  trialEligible: z.boolean(),
  trialEndsAt: z.iso.datetime().meta({ pattern: undefined }).nullable(),
  periodEndsAt: z.iso.datetime().meta({ pattern: undefined }).nullable(),
  paymentMethod: paymentMethodSchema.nullable(),
  monthlyPriceCents: z.literal(1500),
  currency: z.literal('USD'),
  sites: z.array(
    z.object({
      id: idSchema,
      name: z.string(),
      status: z.enum(ServiceStatus),
      cancellationAt: z.iso.datetime().meta({ pattern: undefined }).nullable(),
      paidThrough: z.iso.datetime().meta({ pattern: undefined }).nullable(),
      prepaidUntil: z.iso.datetime().meta({ pattern: undefined }).nullable(),
      renewFrom: z.iso.datetime().meta({ pattern: undefined }).nullable(),
      siteAssistantEnabled: z.boolean(),
      siteAssistantCancellationAt: z.iso.datetime().meta({ pattern: undefined }).nullable(),
    }),
  ),
  operations: z.array(billingOperationSchema),
});
export const invoiceSummarySchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string().nullable(),
  amountDue: z.number().int(),
  amountPaid: z.number().int(),
  currency: z.string(),
  createdAt: z.iso.datetime().meta({ pattern: undefined }),
  pdfUrl: z.url().nullable(),
});

export const billingPaymentSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('Setup'), publishableKey: z.string(), clientSecret: z.string() }),
    z.object({
      kind: z.literal('Payment'),
      publishableKey: z.string(),
      clientSecret: z.string(),
      amountDue: z.number().int(),
      currency: z.string(),
    }),
  ])
  .nullable();
